import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as holiday_jp from "@holiday-jp/holiday_jp";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import "./MonthlyShiftRegistration.css";

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** "09:00:00" → "9:00"（プロジェクトパターンの縦表示用） */
function fmtPatternTime(t) {
  if (!t) return "";
  const [h, m] = String(t).split(":");
  return `${Number(h)}:${m ?? "00"}`;
}

/** 休憩時間（時間数）を "0.75" のような文字列に整形 */
function fmtBreakHours(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : n.toFixed(2);
}

/** holiday_jp.between の戻りは date が Date になるため、ローカル暦で YYYY-MM-DD に揃える */
function toLocalYmd(d) {
  if (d == null || d === "") return "";
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;
}

/** API の時刻を HH:MM 表示用に短縮 */
function formatTimeShort(t) {
  if (t == null || t === "") return "";
  if (typeof t === "string") {
    const m = t.match(/^(\d{1,2}):(\d{2})/);
    if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
    return t.slice(0, 5);
  }
  return String(t);
}

function presetTimeRangeLabel(p) {
  if (!p) return "";
  if (p.is_day_off === true || p.isDayOff === true) return "";
  const st = formatTimeShort(p.start_time ?? p.startTime);
  const et = formatTimeShort(p.end_time ?? p.endTime);
  if (st && et) return `${st}〜${et}`;
  if (st) return `${st}〜`;
  if (et) return `〜${et}`;
  return "";
}

function presetIsDayOff(p) {
  return p && (p.is_day_off === true || p.isDayOff === true);
}

/** マス・プルダウン用：コード（X,Y）と名称（休み・有給・日勤など）をまとめて表示 */
function presetNameLine(p) {
  if (!p) return "";
  const code = (p.code || "").trim();
  const nm = (p.name || p.label || "").trim();
  //if (code && nm && nm !== code) return `${code} ${nm}`;
  if (code && nm && nm !== code) return `${nm}`;
  if (nm) return nm;
  //if (code) return code;
  return `ID:${p.id}`;
}

function presetOptionLabel(p) {
  const line = presetNameLine(p);
  const times = presetTimeRangeLabel(p);
  return times ? `${line}（${times}）` : line;
}

function normalizeMonthlyRows(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  // API が { "YYYY-MM-DD": presetId, ... } の dict を返す場合に対応
  if (!("assignments" in data) && !("shifts" in data) && !("days" in data) && !("items" in data)) {
    const entries = Object.entries(data);
    const looksLikeDateMap = entries.every(([k, v]) => /^\d{4}-\d{2}-\d{2}$/.test(String(k)) && (v === null || v === "" || !Number.isNaN(Number(v))));
    if (looksLikeDateMap) {
      return entries
        .map(([date, shift_preset_id]) => ({ date, shift_preset_id }))
        .filter((r) => r.shift_preset_id != null && r.shift_preset_id !== "");
    }
  }
  return data.assignments || data.shifts || data.days || data.items || [];
}

function rowsToAssignmentMap(rows) {
  const map = {};
  for (const row of rows) {
    const dateStr = (row.date || row.work_date || row.day || "").slice(0, 10);
    const pid =
      row.shift_preset_id ??
      row.shiftPresetId ??
      row.preset_id ??
      row.shift_preset?.id ??
      row.preset?.id;
    if (dateStr && pid != null && pid !== "") map[dateStr] = Number(pid);
  }
  return map;
}

function extractVacationDatesFromApplication(app) {
  let formData = app?.formData;
  if (typeof formData === "string") {
    try {
      formData = JSON.parse(formData);
    } catch {
      return [];
    }
  }
  if (!formData || typeof formData !== "object") return [];
  let selected = formData.selectedDates;
  if (typeof selected === "string") {
    try {
      selected = JSON.parse(selected);
    } catch {
      selected = [];
    }
  }
  if (Array.isArray(selected) && selected.length > 0) {
    return selected.map((d) => String(d).slice(0, 10)).filter(Boolean);
  }
  const single =
    formData.selectedDate ||
    formData.applicationDate ||
    formData.start_date ||
    formData.startDate ||
    "";
  return single ? [String(single).slice(0, 10)] : [];
}

// 休日出勤
function presetIsHolidayWork(p) {
  if (!p) return false;
  const nm = String(p.name || "").toLowerCase();
  const code = String(p.code || "").toLowerCase();
  return nm.includes("休日出勤") || code.includes("休日出勤") || code.includes("holiday_work");
}

// 有給(VACATION)・生理休暇(SERI) の申請から、日付ごとのステータスと種別を作る
function buildLeaveStatusMaps(applications) {
  const statusMap = {};
  const categoryMap = {};
  (applications || []).forEach((app) => {
    if (app.category !== "VACATION" && app.category !== "SERI") return;
    if (app.status !== "SUBMITTED" && app.status !== "APPROVED") return;
    extractVacationDatesFromApplication(app).forEach((date) => {
      if (!statusMap[date] || app.status === "APPROVED") {
        statusMap[date] = app.status;
        categoryMap[date] = app.category;
      }
    });
  });
  return { statusMap, categoryMap };
}

/**
 * 承認済み・申請中の休日出勤申請から「振替休日の日 → もとの出勤日」の対応表を作る。
 * 振替休日のマスに「いつ出勤した分の休みか」を出すために使う。
 */
function buildFurikaeSourceMap(applications) {
  const map = {};
  (applications || []).forEach((app) => {
    if (app.category !== "HOLIDAY_WORK") return;
    if (app.status !== "SUBMITTED" && app.status !== "APPROVED") return;
    const fd = app.formData || {};
    if (!fd.isFurikae) return;
    const workDates = (fd.selectedDates || []).map((d) => String(d).slice(0, 10)).filter(Boolean);
    // substituteDates は複数、substituteDate は1日だけだった頃のデータ
    const subs = Array.isArray(fd.substituteDates) && fd.substituteDates.length > 0
      ? fd.substituteDates
      : [fd.substituteDate];
    subs.map((d) => String(d || "").slice(0, 10)).filter(Boolean).forEach((sub) => {
      map[sub] = [...(map[sub] || []), ...workDates];
    });
  });
  return map;
}

/** 「2026-08-02」→「8/2」 */
function shortMonthDay(dateStr) {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(String(dateStr || ""));
  return m ? `${Number(m[1])}/${Number(m[2])}` : String(dateStr || "");
}

// 休暇種別の表示名（ダイアログ等の説明用）
function leaveTypeLabel(category) {
  return category === "SERI" ? "生理休暇" : "有給";
}

// カレンダーのマス内に出す短縮ラベル（シフトコードの代わりに表示）
function leaveCellLabel(category) {
  return category === "SERI" ? "生休" : "有給";
}

function presetIsPaidLeave(p) {
  if (!p) return false;
  const nm = String(p.name || "").toLowerCase();
  const code = String(p.code || "").toLowerCase();
  return nm.includes("有給") || nm.includes("paid") || code.includes("有給") || code.includes("paid");
}

function presetIsSeiriLeave(p) {
  if (!p) return false;
  const nm = String(p.name || "").toLowerCase();
  const code = String(p.code || "").toLowerCase();
  return (
    nm.includes("生休") ||
    nm.includes("生理") ||
    nm.includes("seiri") ||
    code.includes("生休") ||
    code.includes("生理") ||
    code.includes("seiri")
  );
}

/**
 * @param {object} props
 * @param {string} [props.memberId] 管理画面から開くときの対象社員コード
 * @param {string} [props.name] 見出し用の氏名
 * @param {() => void} props.onBack
 * @param {() => void} [props.onRegistrationComplete] 登録成功時
 */
const MonthlyShiftRegistration = ({
  memberId,
  name,
  onBack,
  onRegistrationComplete,
}) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [presets, setPresets] = useState([]);
  const [empSex, setEmpSex] = useState(null); // 対象社員の性別（'女性' のときのみ生休を表示）
  const [assignments, setAssignments] = useState({});
  /** サーバー取得時の割当（有給ロック日の月次置換時に復元用） */
  const [serverAssignments, setServerAssignments] = useState({});
  const [selectedPreset, setSelectedPreset] = useState("");
  // プロジェクト（選択するとそのプロジェクトのパターン=シフトだけに絞る）
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  // 本人（社員側）: 社員マスターで割り当てられたプロジェクト。これに応じてシフトを自動絞り込みする。
  const [myProject, setMyProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // 休暇申請状態: { "YYYY-MM-DD": "SUBMITTED" | "APPROVED" }（有給・生理休暇）
  const [paidLeaveApps, setPaidLeaveApps] = useState({});
  // 休暇種別: { "YYYY-MM-DD": "VACATION" | "SERI" }
  const [leaveCategoryApps, setLeaveCategoryApps] = useState({});
  // 振替休日: { "振替で休む日": ["もとの出勤日", ...] }
  const [furikaeSource, setFurikaeSource] = useState({});
  const navigate = useNavigate();

  const holidayDates = useMemo(() => {
    const from = new Date(year, month - 1, 1);
    const to = new Date(year, month, 0);
    try {
      const list = holiday_jp.between(from, to);
      return new Set((list || []).map((h) => toLocalYmd(h.date)).filter(Boolean));
    } catch {
      return new Set();
    }
  }, [year, month]);

  const holidayNameByDate = useMemo(() => {
    const from = new Date(year, month - 1, 1);
    const to = new Date(year, month, 0);
    const map = new Map();
    try {
      for (const h of holiday_jp.between(from, to) || []) {
        const key = toLocalYmd(h.date);
        if (key) map.set(key, h.name);
      }
    } catch (_) {}
    return map;
  }, [year, month]);

  const loadMonthly = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { year, month };
      if (memberId) params.memberId = memberId;
      const res = await api.get("/api/shifts/monthly/", { params });
      const rows = normalizeMonthlyRows(res.data);
      const map = rowsToAssignmentMap(rows);
      setAssignments(map);
      setServerAssignments(map);
    } catch (e) {
      setError("月次シフトの取得に失敗しました。");
      setAssignments({});
      setServerAssignments({});
    } finally {
      setLoading(false);
    }
  }, [year, month, memberId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/api/shifts/presets/");
        const list = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) setPresets(list);
      } catch {
        if (!cancelled) setPresets([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // プロジェクト一覧を取得（管理権限が無い場合は 403 → 空配列で無視＝プロジェクト絞り込みなし）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/api/admin/projects/");
        const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
        if (!cancelled) setProjects(list);
      } catch {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 本人（社員側・memberId無し）のときは、社員マスターに割り当てられた
  // プロジェクトを取得し、そのプロジェクトのシフトに自動で絞り込む。
  useEffect(() => {
    if (memberId) { setMyProject(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/api/employee/my-project/");
        if (!cancelled) setMyProject(res.data?.project || null);
      } catch {
        if (!cancelled) setMyProject(null);
      }
    })();
    return () => { cancelled = true; };
  }, [memberId]);

  // 対象社員の性別を取得（生休＝生理休暇を女性のみ表示するため）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (memberId) {
          // 管理者が対象社員を操作中：社員マスタから性別を取得
          const res = await api.get("/api/admin/employee-master/", { params: { q: memberId } });
          const list = res.data?.results ?? res.data ?? [];
          const emp = (Array.isArray(list) ? list : []).find(
            (e) => String(e.memberId) === String(memberId) || String(e.employeeId) === String(memberId)
          );
          if (!cancelled) setEmpSex(emp?.sex ?? null);
        } else {
          // 本人：自分の社員情報から性別を取得
          const res = await api.get("/api/employee/me/");
          const emp = res.data?.employee || res.data;
          if (!cancelled) setEmpSex(emp?.sex ?? null);
        }
      } catch {
        if (!cancelled) setEmpSex(null);
      }
    })();
    return () => { cancelled = true; };
  }, [memberId]);

  // 割当てるシフトの選択肢：生休（生理休暇）は女性のみ表示
  // DB/django-admin は「女」、アプリ旧データは「女性」。どちらも女性として扱う。
  const isFemale = /女/.test(String(empSex || ""));
  // 選択中プロジェクトのパターンに含まれるシフトIDの集合（未選択なら絞り込みなし）
  // 本人（社員側）のときは、社員マスターのプロジェクト(myProject)を自動適用してシフトを絞り込む。
  const selectedProjectObj = (!memberId && myProject)
    ? myProject
    : projects.find((pr) => String(pr.id) === String(selectedProject));
  const projectShiftIds = selectedProjectObj
    ? new Set(
        (selectedProjectObj.patterns || []).flatMap((pt) =>
          (pt.types || []).map((t) => Number(t.shift_id))
        )
      )
    : null;
  // 休日・有給・指定休・生休 などの非勤務シフトは、どのプロジェクトを選んでも常に表示する
  const isAlwaysShownPreset = (p) => {
    if (presetIsDayOff(p) || presetIsPaidLeave(p) || presetIsSeiriLeave(p)) return true;
    const label = `${p.name || ""} ${p.code || ""}`;
    return /休|有給|祝日|祭日/.test(label);
  };
  const visiblePresets = presets
    .filter((p) => isFemale || !presetIsSeiriLeave(p))
    .filter((p) => !projectShiftIds || projectShiftIds.has(Number(p.id)) || isAlwaysShownPreset(p));

  useEffect(() => {
    if (selectedPreset === "" && visiblePresets.length) {
      setSelectedPreset(String(visiblePresets[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presets, empSex, selectedPreset]);

  useEffect(() => {
    loadMonthly();
  }, [loadMonthly]);

  // 有給申請状態を取得（管理者モードでは自分の申請なので管理者用APIは使わない）
  useEffect(() => {
    if (memberId) {
      // 管理者側で他の社員を操作している場合はスキップ
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/api/employee/applications/");
        const apps = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) {
          const { statusMap, categoryMap } = buildLeaveStatusMaps(apps);
          setPaidLeaveApps(statusMap);
          setLeaveCategoryApps(categoryMap);
          setFurikaeSource(buildFurikaeSourceMap(apps));
        }
      } catch (_) {
        if (!cancelled) {
          setPaidLeaveApps({});
          setLeaveCategoryApps({});
          setFurikaeSource({});
        }
      }
    })();
    return () => { cancelled = true; };
  }, [memberId]);

  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
  }, []);

  const calendarCells = useMemo(() => {
    const firstDow = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const totalCells = Math.ceil((firstDow + daysInMonth) / 7) * 7; // 35 or 42
    const cells = [];
    let d = 1;
    for (let i = 0; i < totalCells; i += 1) {
      if (i < firstDow || d > daysInMonth) {
        cells.push({ empty: true, key: `e-${i}` });
      } else {
        const dateStr = `${year}-${pad2(month)}-${pad2(d)}`;
        cells.push({ empty: false, day: d, dateStr, key: dateStr });
        d += 1;
      }
    }
    return cells;
  }, [year, month]);

  const presetById = (id) => presets.find((x) => Number(x.id) === Number(id));

  const isDateInViewMonth = useCallback(
    (dateStr) => {
      if (!dateStr) return false;
      const [y, m] = dateStr.split("-").map(Number);
      return y === year && m === month;
    },
    [year, month]
  );

  const isPaidLeaveLocked = useCallback(
    (dateStr) => Boolean(!memberId && paidLeaveApps[dateStr]),
    [memberId, paidLeaveApps]
  );

  // 有給申請中・承認済みの日に他シフトが残っていたら除去
  useEffect(() => {
    if (memberId || !Object.keys(paidLeaveApps).length) return;
    setAssignments((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [dateStr, pid] of Object.entries(next)) {
        if (!paidLeaveApps[dateStr]) continue;
        const p = presets.find((x) => Number(x.id) === Number(pid));
        if (!presetIsPaidLeave(p)) {
          delete next[dateStr];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [paidLeaveApps, presets, memberId]);

  /**
   * クリアモード：その日の割当を削除。
   * シフト選択中：同じシフトが付いているマスをもう一度タップ → 削除（取り消し）。
   * 別のシフトが付いていれば → 上書き。
   * 有給申請中・承認済みの日は確認ダイアログの上でキャンセル（通常出勤へ変更）できる。
   */
  const handleCellClick = async (dateStr) => {
    if (!dateStr) return;
    if (isPaidLeaveLocked(dateStr)) {
      // 有給・生理休暇（申請中・承認済み）の日：確認の上で申請を取り消し、通常シフトに変更できる
      const category = leaveCategoryApps[dateStr] || "VACATION";
      const typeLabel = leaveTypeLabel(category);
      if (!window.confirm(`${typeLabel}申請を取り消し、スケジュールを変更しますか？`)) return;
      // サーバー側の申請を取消（CANCELLED）にする → 管理画面の取消一覧に反映される
      try {
        await api.post("/api/schedule/cancel-leave-day/", {
          date: dateStr,
          leave_type: category,
        });
      } catch (e) {
        window.alert("申請の取り消しに失敗しました。時間をおいて再度お試しください。");
        return;
      }
      // 休暇ロックを解除
      setPaidLeaveApps((prev) => {
        const next = { ...prev };
        delete next[dateStr];
        return next;
      });
      setLeaveCategoryApps((prev) => {
        const next = { ...prev };
        delete next[dateStr];
        return next;
      });
      // 選択中のシフトを反映（未選択・クリアならその日を空にする）
      setAssignments((prev) => {
        const next = { ...prev };
        if (selectedPreset === "" || selectedPreset === "__clear__") {
          delete next[dateStr];
          return next;
        }
        next[dateStr] = Number(selectedPreset);
        return next;
      });
      return;
    }
    setAssignments((prev) => {
      const next = { ...prev };
      if (selectedPreset === "__clear__") {
        delete next[dateStr];
        return next;
      }
      if (selectedPreset === "") return prev;
      const pid = Number(selectedPreset);
      const current = prev[dateStr];
      if (current != null && Number(current) === pid) {
        delete next[dateStr];
        return next;
      }
      next[dateStr] = pid;
      return next;
    });
  };

  const goPrevMonth = () => {
    if (month <= 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
  };

  const goNextMonth = () => {
    if (month >= 12) {
      setMonth(1);
      setYear((y) => y + 1);
    } else setMonth((m) => m + 1);
  };

  const setThisMonth = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth() + 1);
  };

  const setNextMonth = () => {
    const t = new Date();
    t.setDate(1);
    t.setMonth(t.getMonth() + 1);
    setYear(t.getFullYear());
    setMonth(t.getMonth() + 1);
  };

  const isThisMonth =
    year === new Date().getFullYear() && month === new Date().getMonth() + 1;
  const isNextMonthQuick = () => {
    const t = new Date();
    t.setDate(1);
    t.setMonth(t.getMonth() + 1);
    return year === t.getFullYear() && month === t.getMonth() + 1;
  };

  const buildSubmitShifts = useCallback(() => {
    const byId = new Map((presets || []).map((p) => [Number(p.id), p]));
    const isPaidLeavePresetId = (id) => presetIsPaidLeave(byId.get(Number(id)));

    const lockedConflict = Object.entries(assignments).find(
      ([dateStr, id]) =>
        isPaidLeaveLocked(dateStr) &&
        id != null &&
        id !== "" &&
        !isPaidLeavePresetId(id)
    );
    if (lockedConflict) {
      return { error: "有給申請中または承認済みの日に、他のシフトが含まれています。該当日の割当を解除してから登録してください。" };
    }

    const seen = new Set();
    const shifts = [];

    for (const [dateStr, id] of Object.entries(assignments)) {
      if (!isDateInViewMonth(dateStr) || id == null || id === "") continue;
      if (isPaidLeaveLocked(dateStr)) continue;
      shifts.push({ date: dateStr, shift_preset_id: Number(id) });
      seen.add(dateStr);
    }

    for (const dateStr of Object.keys(paidLeaveApps)) {
      if (!isDateInViewMonth(dateStr) || seen.has(dateStr)) continue;
      const sid = serverAssignments[dateStr];
      if (sid != null && sid !== "") {
        shifts.push({ date: dateStr, shift_preset_id: Number(sid) });
        seen.add(dateStr);
      }
    }

    return { shifts };
  }, [
    assignments,
    presets,
    paidLeaveApps,
    serverAssignments,
    isDateInViewMonth,
    isPaidLeaveLocked,
  ]);

  const handleSubmit = async () => {
    const built = buildSubmitShifts();
    if (built.error) {
      setError(built.error);
      return;
    }
    const shifts = built.shifts;

    if (shifts.length === 0) {
      if (!window.confirm("登録する日がありません。このまま送信すると既存の登録がクリアされる場合があります。続行しますか？")) {
        return;
      }
    } else if (!window.confirm(`${year}年${month}月のシフトを登録しますか？`)) {
      return;
    }

    setSaving(true);
    setError("");
    try {
      const body = { year, month, shifts };
      if (memberId) body.memberId = memberId;
      await api.post("/api/shifts/register/monthly/", body);
      const hasPaidLeave = shifts.some((s) =>
        presetIsPaidLeave(presets.find((p) => Number(p.id) === Number(s.shift_preset_id)))
      );

      // memberId がある＝管理者が「他の社員の」スケジュールを編集している状態。
      // このまま社員用の申請フォームへ飛ばすと、申請がログイン中の管理者名義で作られてしまう
      // （申請APIは常に request.user から申請者を決めるため）。管理者は代理申請を使う。
      const isProxyEdit = Boolean(memberId);

      if (hasPaidLeave && isProxyEdit) {
        alert(
          "有給のシフトを登録しました。\n" +
            "有給の消化（残日数）に反映するには、申請管理の「＋ 代理申請」から有給休暇を登録してください。"
        );
        if (onRegistrationComplete) {
          onRegistrationComplete();
          return;
        }
        navigate("/admin/applications");
        return;
      }

      if (hasPaidLeave) {
        // 有給日付を localStorage に保存して有給休暇申請へ
        const paidLeaveDates = shifts
          .filter((s) =>
            presetIsPaidLeave(presets.find((p) => Number(p.id) === Number(s.shift_preset_id)))
          )
          .map((s) => s.date);
        if (paidLeaveDates.length > 0) {
          localStorage.setItem("calendar_paid_leave_dates", JSON.stringify(paidLeaveDates));
        }
        navigate("/selected-forms?ids=paid_leave");
        return;
      }

      // 生休（生理休暇）が含まれる場合は生理休暇申請へ
      const hasSeiriLeave = shifts.some((s) =>
        presetIsSeiriLeave(presets.find((p) => Number(p.id) === Number(s.shift_preset_id)))
      );
      if (hasSeiriLeave && isProxyEdit) {
        // 有給と同じ理由（管理者名義の申請になってしまう）でフォームへは飛ばさない
        alert("生理休暇のシフトを登録しました。申請が必要な場合は社員本人に申請してもらってください。");
        if (onRegistrationComplete) {
          onRegistrationComplete();
          return;
        }
        navigate("/admin/applications");
        return;
      }

      if (hasSeiriLeave) {
        const seiriLeaveDates = shifts
          .filter((s) =>
            presetIsSeiriLeave(presets.find((p) => Number(p.id) === Number(s.shift_preset_id)))
          )
          .map((s) => s.date);
        if (seiriLeaveDates.length > 0) {
          localStorage.setItem("calendar_seiri_leave_dates", JSON.stringify(seiriLeaveDates));
        }
        navigate("/selected-forms?ids=seiri_leave");
        return;
      }

      // 休日出勤
      const hasHolidayWork = shifts.some((s) =>
        presetIsHolidayWork(presets.find((p) => Number(p.id) === Number(s.shift_preset_id)))
      );
      if (hasHolidayWork && !isProxyEdit) {
        const holidayWorkDates = shifts
          .filter((s) => presetIsHolidayWork(presets.find((p) => Number(p.id) === Number(s.shift_preset_id))))
          .map((s) => s.date);
        localStorage.setItem("calendar_holiday_work_dates", JSON.stringify(holidayWorkDates));
        
        // 申請画面に 休日出勤の申請フォームを開くためのパラメータを付与して遷移
        navigate("/selected-forms?ids=holiday_work");
        return;
      }

      // 有給なし: 確認ダイアログなしで社員証へ（親が onRegistrationComplete を渡していればそれを優先）
      if (onRegistrationComplete) {
        onRegistrationComplete();
        return;
      }
      navigate("/card_back");
    } catch (e) {
      setError(e.response?.data?.message || "登録に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const headerNote = memberId || name ? ` — ${name || memberId || ""}` : "";

  return (
    <div className="shift-registration-container">
      <h2 className="shift-registration-page-title" style={{ marginBottom: 8 }}>
        スケジュール登録{headerNote}
      </h2>

      <div className="shift-month-nav">
        <button type="button" className="shift-month-btn" onClick={goPrevMonth}>
          前月
        </button>
        <span className="shift-month-title">
          {year}年{month}月
        </span>
        <button type="button" className="shift-month-btn" onClick={goNextMonth}>
          翌月
        </button>
      </div>

      <div className="shift-quick-btns">
        <button
          type="button"
          className={`shift-quick-btn${isThisMonth ? " active" : ""}`}
          onClick={setThisMonth}
        >
          今月
        </button>
        <button
          type="button"
          className={`shift-quick-btn${isNextMonthQuick() ? " active" : ""}`}
          onClick={setNextMonth}
        >
          来月
        </button>
      </div>

      <div className="shift-toolbar">
        {projects.length > 0 && (
          <>
            <label htmlFor="shift-project-select">プロジェクト</label>
            <select
              id="shift-project-select"
              value={selectedProject}
              onChange={(e) => { setSelectedProject(e.target.value); setSelectedPreset(""); }}
            >
              <option value="">すべて</option>
              {projects.map((pr) => (
                <option key={pr.id} value={String(pr.id)}>{pr.project_name}</option>
              ))}
            </select>
          </>
        )}
        <label htmlFor="shift-preset-select">割当てるシフト</label>
        <select
          id="shift-preset-select"
          value={selectedPreset}
          onChange={(e) => setSelectedPreset(e.target.value)}
          disabled={!presets.length}
        >
          <option value="">選択してください</option>
          {visiblePresets.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {presetOptionLabel(p)}
            </option>
          ))}
          <option value="__clear__">— クリックした日の割当を消す —</option>
        </select>
        {selectedPreset && selectedPreset !== "__clear__" && (
          <span className="shift-active-hint">
            日付枠をタップで反映。同じ枠をもう一度タップで取り消し。
          </span>
        )}
        {selectedPreset === "__clear__" && (
          <span className="shift-active-hint">日付枠をタップしてクリア</span>
        )}
      </div>

      {/* 選択したプロジェクトのパターン（タイプを縦に表示）。
          社員側（memberId無し）では「勤務地：/プロジェクト：/シフト一覧」の情報パネルは非表示にする。 */}
      {memberId && selectedProjectObj && (selectedProjectObj.patterns || []).length > 0 && (
        <div className="shift-pattern-panel" style={{ margin: "8px 0", padding: "8px 12px", border: "1px solid #d0d7de", borderRadius: "6px", background: "#f6f8fa" }}>
          <div style={{ fontWeight: "bold", marginBottom: "6px" }}>
            勤務地：{selectedProjectObj.work_location_name || ""}　/　プロジェクト：{selectedProjectObj.project_name}
          </div>
          {(selectedProjectObj.patterns || []).map((pt) => (
            <div key={pt.pattern_number} style={{ marginBottom: "6px" }}>
              <div style={{ fontSize: "12px", color: "#666" }}>パターン{pt.pattern_number}</div>
              <table className="shift-pattern-table" style={{ borderCollapse: "collapse" }}>
                <tbody>
                  {(pt.types || []).map((t) => (
                    <tr key={t.type_number}>
                      <td style={{ padding: "1px 10px 1px 0", fontWeight: "bold" }}>{t.shift_code || t.shift_name || ""}</td>
                      <td style={{ padding: "1px 10px 1px 0" }}>{fmtPatternTime(t.start_time)}</td>
                      <td style={{ padding: "1px 10px 1px 0" }}>{fmtPatternTime(t.end_time)}</td>
                      <td style={{ padding: "1px 0" }}>{fmtBreakHours(t.break_hours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {error ? (
        <p style={{ color: "#c62828", textAlign: "center" }} role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p style={{ textAlign: "center" }}>読み込み中…</p>
      ) : (
        <table className="calendar-table calendar-table-fixed">
          <thead>
            <tr>
              {["日", "月", "火", "水", "木", "金", "土"].map((w, idx) => (
                <th
                  key={w}
                  className={idx === 0 ? "header-sunday" : idx === 6 ? "header-saturday" : ""}
                >
                  {w}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }, (_, row) => (
              <tr key={row}>
                {calendarCells.slice(row * 7, row * 7 + 7).map((cell) => {
                  if (cell.empty) {
                    return (
                      <td key={cell.key} className="empty-cell" aria-hidden="true" />
                    );
                  }
                  const { dateStr, day } = cell;
                  const dow = new Date(year, month - 1, day).getDay();
                  const isHoliday = holidayDates.has(dateStr);
                  const classNames = ["calendar-cell"];
                  if (isHoliday || dow === 0) classNames.push("sunday");
                  else if (dow === 6) classNames.push("saturday");
                  if (dateStr === todayStr) classNames.push("today");
                  const pid = assignments[dateStr];
                  const pAssigned = pid != null ? presetById(pid) : null;
                  const off = pAssigned && presetIsDayOff(pAssigned);
                  const assignedTimeStr = pAssigned ? presetTimeRangeLabel(pAssigned) : "";
                  if (off) classNames.push("calendar-cell--off");

                  // 振替休日の日は、もとの休日出勤日を出す（いつ出勤した分の休みか分かるように）
                  const furikaeWorkDates = furikaeSource[dateStr] || [];

                  // 有給申請状態
                  const paidLeaveStatus = paidLeaveApps[dateStr];
                  const paidLeaveLocked = Boolean(paidLeaveStatus);
                  // 承認済みで過去日（既に取得済み）の場合は「（承認済み）」を出さず「有給」のみ表示
                  const isPastApproved =
                    paidLeaveStatus === "APPROVED" && dateStr < todayStr;
                  if (paidLeaveStatus === "SUBMITTED") classNames.push("calendar-cell--paid-leave-pending");
                  else if (paidLeaveStatus === "APPROVED") classNames.push("calendar-cell--paid-leave-approved");
                  if (paidLeaveLocked) classNames.push("calendar-cell--paid-leave-locked");

                  const cellProps = {
                    role: "button",
                    tabIndex: 0,
                    onClick: () => handleCellClick(dateStr),
                    onKeyDown: (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCellClick(dateStr);
                      }
                    },
                    ...(paidLeaveLocked
                      ? { title: "クリックでスケジュールを変更できます" }
                      : {}),
                  };

                  return (
                    <td
                      key={cell.key}
                      className={classNames.join(" ")}
                      {...cellProps}
                    >
                      <div className="calendar-cell-inner">
                        <div className="date-number">{day}</div>
                        {isHoliday ? (
                          <div className="shift-holiday-label">
                            {holidayNameByDate.get(dateStr) || "祝日"}
                          </div>
                        ) : null}
                        {pAssigned ? (
                          <div className="shift-cell-assignment">
                            <div className="shift-cell-line1">
                              {paidLeaveStatus
                                ? leaveCellLabel(leaveCategoryApps[dateStr])
                                : presetNameLine(pAssigned)}
                            </div>
                            {paidLeaveStatus === "SUBMITTED" ? (
                              <div className="shift-cell-paid-label shift-cell-paid-label--pending">
                                （申請中）
                              </div>
                            ) : paidLeaveStatus === "APPROVED" && !isPastApproved ? (
                              <div className="shift-cell-paid-label shift-cell-paid-label--approved">
                                （承認済）
                              </div>
                            ) : !paidLeaveStatus && assignedTimeStr ? (
                              <div className="shift-cell-line2">{assignedTimeStr}</div>
                            ) : null}
                            {furikaeWorkDates.length > 0 ? (
                              <div className="shift-cell-line2">
                                {furikaeWorkDates.map(shortMonthDay).join("・")} 出勤分
                              </div>
                            ) : null}
                          </div>
                        ) : paidLeaveStatus ? (
                          <div className="shift-cell-assignment">
                            <div className={`shift-cell-paid-label shift-cell-paid-label--${paidLeaveStatus === "SUBMITTED" ? "pending" : "approved"}`}>
                              {leaveCellLabel(leaveCategoryApps[dateStr])}
                            </div>
                            {paidLeaveStatus === "SUBMITTED" ? (
                              <div className="shift-cell-paid-label shift-cell-paid-label--pending">
                                （申請中）
                              </div>
                            ) : !isPastApproved ? (
                              <div className="shift-cell-paid-label shift-cell-paid-label--approved">
                                （承認済）
                              </div>
                            ) : null}
                          </div>
                        ) : furikaeWorkDates.length > 0 ? (
                          // 申請中などでまだ振替休日のシフトが割り当たっていない日
                          <div className="shift-cell-assignment">
                            <div className="shift-cell-line1">振替休日</div>
                            <div className="shift-cell-line2">
                              {furikaeWorkDates.map(shortMonthDay).join("・")} 出勤分
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="shift-footer-btns">
        <button type="button" className="shift-btn-submit" disabled={saving} onClick={handleSubmit}>
          {saving ? "送信中…" : "登録"}
        </button>
        <button type="button" className="shift-btn-back" onClick={() => {
          if (onBack) {
            onBack();
          } else {
            navigate("/card_back");
          }
        }}
        >
          戻る
        </button>
      </div>
    </div>
  );
};

export default MonthlyShiftRegistration;
